import React, { useContext, useEffect, useState } from 'react';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import { intersection } from 'lodash';
import { Box, List, ListItem, ListItemIcon, ListItemText, Checkbox, Button, Paper, Grid, Typography } from '@mui/material';
import { BrandContext } from '../../../contexts/BrandContext';
import { cLog } from '../CustomLogger';


export default function DragList(props) {
    const brandContext = useContext(BrandContext);

    const {
        textContent
    } = brandContext;
    
    const {
        formFields=[],
        fieldName='',
        formik,
        optionsId=0
    } = props;

    let value = []
    if(formik.values[fieldName]){
        if(!Array.isArray(formik.values[fieldName])){
            value = formik.values[fieldName].split(',');
        }
        else{
            value = formik.values[fieldName];
        }
    }

    const fieldProps = formFields[fieldName];
        
    const {
        label:labelKey='',
        assetPath='',
        fileName='',
        options=null
    } = fieldProps;

    let defaultOptionsMap = null;
    let defaultLeft = [];
    if(optionsId in options){
        defaultOptionsMap = options[optionsId];
        defaultLeft = Object.keys(options[optionsId]).filter(item => !value.includes(item));
    }

    const [checked, setChecked] = useState([]);
    const [optionsMap, setOptionsMap] = useState(defaultOptionsMap);
    const [left, setLeft] = useState(defaultLeft);
    const [right, setRight] = useState(value);

    if(!(fieldName in formFields)){
        return null;
    }
    
    
    const label = textContent[labelKey];

    
    let errorText = '';
    if(formik.errors[fieldName]){
        errorText = formik.errors[fieldName];
    }



    /* eslint-disable */
    useEffect(() => {
        if(optionsId in options){
            const newOptionsMap = options[optionsId];
            const newOptionsList = Object.keys(newOptionsMap);
            setOptionsMap(newOptionsMap);
            setLeft(newOptionsList.filter(item => !value.includes(item)));
            setRight(right.filter(item => newOptionsList.includes(item)));
        }
    },[optionsId]);
    /* eslint-enable */

    const leftChecked = intersection(checked, left);
    const rightChecked = intersection(checked, right);

    // Handle the checkbox toggle for an item
    const handleToggle = (value) => () => {
        const currentIndex = checked.indexOf(value);
        const newChecked = [...checked];

        if (currentIndex === -1) {
        newChecked.push(value);
        } else {
        newChecked.splice(currentIndex, 1);
        }

        setChecked(newChecked);
    };

    // Transfer all items to the right
    const handleAllRight = () => {
        const rightVal = right.concat(left);
        setRight(rightVal);
        formik.setFieldValue(fieldName, rightVal);
        setLeft([]);
    };

    // Transfer selected items to the right
    const handleCheckedRight = () => {
        const rightVal = right.concat(leftChecked);
        setRight(rightVal);
        formik.setFieldValue(fieldName, rightVal);
        setLeft(not(left, leftChecked));
        setChecked(not(checked, leftChecked));
    };

    // Transfer selected items to the left
    const handleCheckedLeft = () => {
        setLeft(left.concat(rightChecked));
        const rightVal = not(right, rightChecked);
        setRight(rightVal);
        formik.setFieldValue(fieldName, rightVal);
        setChecked(not(checked, rightChecked));
    };

    // Transfer all items to the left
    const handleAllLeft = () => {
        setLeft(left.concat(right));
        const rightVal = [];
        setRight(rightVal);
        formik.setFieldValue(fieldName, rightVal);
    };

    // Drag-and-drop handler for reordering
    const handleOnDragEnd = (result) => {
        if (!result.destination) return;

        const updatedList = Array.from(right);
        const [reorderedItem] = updatedList.splice(result.source.index, 1);
        updatedList.splice(result.destination.index, 0, reorderedItem);

        setRight(updatedList);
    };

    const not = (a, b) =>  a.filter((value) => b.indexOf(value) === -1);
    
   

    // Custom list component for rendering the transfer list
    const customList = (items, isRightList = false) => (
        <>
        {items && 
        <Paper style={{ width: 400, height: 500, overflow: 'auto' }}>
        {isRightList ? (
            // Right list with drag-and-drop reorder enabled
            <DragDropContext onDragEnd={handleOnDragEnd}>
            <Droppable droppableId="droppable-list">
                {(provided) => (
                <List dense component="div" role="list" {...provided.droppableProps} ref={provided.innerRef}>
                    {items.map((value, index) => {
                    const labelId = `transfer-list-item-${value}-label`;
                    return (
                        <Draggable key={value} draggableId={value.toString()} index={index}>
                        {(provided) => (
                            <ListItem
                            ref={provided.innerRef}
                            {...provided.draggableProps}
                            {...provided.dragHandleProps}
                            role="listitem"
                            >
                            <ListItemIcon>
                                <Checkbox
                                checked={checked.indexOf(value) !== -1}
                                tabIndex={-1}
                                disableRipple
                                inputProps={{ 'aria-labelledby': labelId }}
                                onClick={handleToggle(value)}
                                />
                            </ListItemIcon>
                            <ListItemText id={labelId} primary={`${optionsMap[value]}`} />
                            </ListItem>
                        )}
                        </Draggable>
                    );
                    })}
                    {provided.placeholder}
                </List>
                )}
            </Droppable>
            </DragDropContext>
        ) : (
            // Left list without drag-and-drop
            <List dense component="div" role="list">
            {items.map((value) => {
                const labelId = `transfer-list-item-${value}-label`;
                return (
                <ListItem key={value} role="listitem" button onClick={handleToggle(value)}>
                    <ListItemIcon>
                    <Checkbox
                        checked={checked.indexOf(value) !== -1}
                        tabIndex={-1}
                        disableRipple
                        inputProps={{ 'aria-labelledby': labelId }}
                    />
                    </ListItemIcon>
                    <ListItemText id={labelId} primary={`${optionsMap[value]}`} />
                </ListItem>
                );
            })}
            </List>
        )}
        </Paper>
        }
        </>
    );
    

    return (
        <Box>
            <Typography>{label}</Typography>
        <Grid container spacing={2} justifyContent="center" alignItems="center">
            <Grid item>{customList(left)}</Grid>
            <Grid item>
                <Grid container direction="column" alignItems="center">
                <Button
                    sx={{ my: 1 }}
                    variant="contained"
                    size="small"
                    onClick={handleAllRight}
                    disabled={left.length === 0}
                    aria-label="move all right"
                >
                    ≫
                </Button>
                <Button
                    sx={{ my: 1 }}
                    variant="contained"
                    size="small"
                    onClick={handleCheckedRight}
                    disabled={leftChecked.length === 0}
                    aria-label="move selected right"
                >
                    &gt;
                </Button>
                <Button
                    sx={{ my: 1 }}
                    variant="contained"
                    size="small"
                    onClick={handleCheckedLeft}
                    disabled={rightChecked.length === 0}
                    aria-label="move selected left"
                >
                    &lt;
                </Button>
                <Button
                    sx={{ my: 1 }}
                    variant="contained"
                    size="small"
                    onClick={handleAllLeft}
                    disabled={right.length === 0}
                    aria-label="move all left"
                >
                    ≪
                </Button>
                </Grid>
            </Grid>
            <Grid item>{customList(right, true)}</Grid>
        </Grid>
        </Box>
    );
}